import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DateInput from '../forms/dateInput';
import { useQuery, useQueryClient } from 'react-query';
import { DOCUMENT_LABELS } from '../../constants/labels';
import { getBlankReceiptPdf, getDocumentPdf, listJobDocuments, saveWorkOrder, uploadReceiptScan } from '../../services/documents';
import { getInvoicePdf } from '../../services/email';
import InvoiceSharePanel from '../invoiceSharePanel';
import DocumentSharePanel from './documentSharePanel';
import PickupReceiptModal from './pickupReceiptModal';
import TrackingLinkControl from '../tracking/trackingLinkControl';
import { isOfficeUser } from '../../utils/roleHelpers';
import i18n from '../../i18n/i18n';
import './documents.css';

const ALL_TYPES = ['INVOICE', 'PICKUP_RECEIPT', 'WORK_ORDER'];

const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

// Opens a PDF blob in a new tab; for printing it also asks the tab to print
// once the viewer has loaded (browsers that ignore it still show the PDF).
const openBlob = (blob, { print = false } = {}) => {
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    // Thrown, not alerted: run() catches it and shows the panel's own error
    // banner, which is where every other document failure already appears.
    if (!win) throw new Error(i18n.t('jobDocuments.pdfBlocked'));
    if (print) {
        setTimeout(() => { try { win.focus(); win.print(); } catch (_) { /* viewer without print API */ } }, 1200);
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
};

/**
 * Documents section of the job drawer: Invoice / Pickup Receipt / Work Order
 * with Generate / View PDF / Print / Send. Implemented with items 3.4–3.6.
 *
 * Props: { jobId, job, client } — `client` is the job's client (name, phone,
 * email) so Send panels can prefill the recipient.
 */
// An API error's message is not always a string: a 500 used to arrive as an
// object, which React rendered as "[object Object]" and told the user nothing.
const errorText = (e) => {
    const raw = e?.response?.data?.message ?? e?.message ?? e;
    // A browser-launch failure arrives as hundreds of lines of crash dump;
    // show the first line or two rather than flooding the drawer.
    const clamp = (t) => (t.length > 300 ? `${t.slice(0, 300).trimEnd()}…` : t);
    if (typeof raw === 'string' && raw.trim()) return clamp(raw.trim());
    if (raw && typeof raw === 'object') {
        if (typeof raw.message === 'string') return clamp(raw.message);
        try { return JSON.stringify(raw); } catch { /* fall through */ }
    }
    return i18n.t('toaster.error');
};

/** Secondary document action: icon only, but always labelled for screen readers
 *  and on hover. Three rows x (open, print, send) as full-width buttons made the
 *  panel read as a wall of identical controls. */
const IconButton = ({ icon, label, hint, disabled, busy, onClick }) => (
    <button type="button" className="jdoc-icon" onClick={onClick} disabled={disabled}
        title={hint || label} aria-label={label}>
        <i className={`fa-solid ${busy ? 'fa-spinner fa-spin' : icon}`} aria-hidden="true"></i>
    </button>
);

const JobDocumentsSection = ({ jobId, job, client, onSendTracking }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [receiptOpen, setReceiptOpen] = useState(false);
    const [invoiceToSend, setInvoiceToSend] = useState(null);
    const [documentToSend, setDocumentToSend] = useState(null);
    const [completionOpen, setCompletionOpen] = useState(false);
    const [completion, setCompletion] = useState(null);
    const fileRef = useRef(null);

    const { data, isLoading } = useQuery(['job-documents', jobId], () => listJobDocuments(jobId), { enabled: !!jobId });
    const docs = data?.code === 200 && Array.isArray(data.message) ? data.message : [];
    const refresh = () => queryClient.invalidateQueries(['job-documents', jobId]);
    const hasLineItems = Array.isArray(job?.lineItems) ? job.lineItems.length > 0 : Array.isArray(job?.lineItemsTemp) ? job.lineItemsTemp.length > 0 : false;
    const TYPES = hasLineItems ? ALL_TYPES : ALL_TYPES.filter(t => t !== 'INVOICE');

    const invoiceDoc = docs.find((d) => d.type === 'INVOICE');
    const receiptDigital = docs.find((d) => d.type === 'PICKUP_RECEIPT' && d.source === 'DIGITAL' && d.filePath);
    const receiptPaper = docs.find((d) => d.type === 'PICKUP_RECEIPT' && d.source === 'PAPER_SCAN' && d.filePath);
    const workOrderDoc = docs.find((d) => d.type === 'WORK_ORDER');
    // Null until the invoice is first opened or sent; the API issues it then.
    const invoiceNumber = invoiceDoc?.number || job?.invoiceNumber || null;

    const run = async (key, fn) => {
        setBusy(key);
        setError('');
        try { await fn(); } catch (e) { setError(errorText(e)); } finally { setBusy(''); }
    };

    const openPdf = (type, print, source) => run(`${type}-${print ? 'print' : 'open'}`, async () => {
        const blob = type === 'INVOICE' ? await getInvoicePdf(jobId) : await getDocumentPdf(jobId, type, { source });
        openBlob(blob, { print });
        // Opening the invoice issues its number; opening a work order creates it.
        if ((type === 'WORK_ORDER' && !workOrderDoc) || (type === 'INVOICE' && !invoiceNumber)) refresh();
    });

    const openBlank = (size) => run(`blank-${size}`, async () => openBlob(await getBlankReceiptPdf(jobId, size), { print: true }));

    const onScanFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        run('scan', async () => {
            const response = await uploadReceiptScan(jobId, file);
            if (response?.code !== 200) throw new Error(typeof response?.message === 'string' ? response.message : t('jobDocuments.uploadFailed'));
            refresh();
        });
    };

    const sendReceipt = () => {
        const doc = receiptDigital || receiptPaper;
        if (doc) setDocumentToSend(doc);
    };

    const sendWorkOrder = () => run('WORK_ORDER-send', async () => {
        let doc = workOrderDoc;
        if (!doc) {
            const response = await saveWorkOrder(jobId, {});
            if (response?.code !== 200) throw new Error(typeof response?.message === 'string' ? response.message : t('jobDocuments.workOrderCouldNotBeCreated'));
            doc = response.message;
            refresh();
        }
        setDocumentToSend(doc);
    });

    const openCompletion = () => {
        const snap = workOrderDoc?.snapshot || {};
        setCompletion({
            completedAt: toLocalInput(snap.completion?.completedAt || job?.completionTimeActual),
            completedBy: snap.completion?.completedBy || '',
            workPerformed: snap.completion?.workPerformed || '',
            notes: snap.completion?.notes || '',
            employeeName: snap.confirmations?.employeeName || '',
            customerName: snap.confirmations?.customerName || [client?.firstName, client?.lastName].filter(Boolean).join(' '),
            customerRemarks: snap.confirmations?.customerRemarks || '',
        });
        setCompletionOpen(true);
    };

    // This panel renders inside the job edit form (orderEditTemp.jsx wraps
    // everything in <Form id="edit-job" onSubmit={handleSubmit}>). It used to be
    // a nested <form> with a submit button: the submit event bubbles, so saving
    // completion details also submitted the job form — the job was saved and the
    // drawer torn down, which looked like the typed details vanishing. It is a
    // plain div now, and the handler stops the event reaching the outer form.
    const saveCompletion = (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        run('WORK_ORDER-save', async () => {
            const now = new Date().toISOString();
            const prev = workOrderDoc?.snapshot?.confirmations || {};
            const response = await saveWorkOrder(jobId, {
                completion: {
                    completedAt: fromLocalInput(completion.completedAt),
                    completedBy: completion.completedBy || null,
                    workPerformed: completion.workPerformed || null,
                    notes: completion.notes || null,
                },
                confirmations: {
                    employeeName: completion.employeeName || null,
                    employeeConfirmedAt: completion.employeeName ? (prev.employeeConfirmedAt || now) : null,
                    customerName: completion.customerName || null,
                    customerConfirmedAt: completion.customerName ? (prev.customerConfirmedAt || now) : null,
                    customerRemarks: completion.customerRemarks || null,
                },
            });
            if (response?.code !== 200) throw new Error(typeof response?.message === 'string' ? response.message : t('jobDocuments.workOrderCouldNotBeSaved'));
            setCompletionOpen(false);
            refresh();
        });
    };

    if (!jobId) return null;

    const statusOf = (type) => {
        if (type === 'INVOICE') {
            return invoiceNumber
                ? { ok: true, label: t('jobDocuments.ready'), meta: `${invoiceNumber} · ${t('jobDocuments.alwaysMatchesJob')}` }
                : { ok: true, label: t('jobDocuments.ready'), meta: t('jobDocuments.numberedFirstTime') };
        }
        if (type === 'PICKUP_RECEIPT') {
            if (receiptDigital) return { ok: true, label: t('jobDocuments.signed'), meta: `${receiptDigital.number || ''} · ${t('jobDocuments.signedBy', { name: receiptDigital.signedByName || t('jobDocuments.customerFallback'), date: fmt(receiptDigital.updatedAt) })}${receiptPaper ? ' · ' + t('jobDocuments.paperScanAttached') : ''}` };
            if (receiptPaper) return { ok: true, label: t('jobDocuments.paperAttached'), meta: `${receiptPaper.number || ''} · ${t('jobDocuments.scannedOn', { date: fmt(receiptPaper.updatedAt) })}` };
            return { ok: false, label: t('jobDocuments.notGenerated'), meta: t('jobDocuments.signOrPrint') };
        }
        if (workOrderDoc) {
            const c = workOrderDoc.snapshot?.completion || {};
            const done = c.completedAt || c.completedBy;
            return { ok: true, label: done ? t('jobDocuments.completed') : t('jobDocuments.generated'), meta: `${workOrderDoc.number || ''} · ${t('jobDocuments.generatedOn', { date: fmt(workOrderDoc.createdAt) })}${done ? ' · ' + t('jobDocuments.completionRecorded') : ''}` };
        }
        return { ok: false, label: t('jobDocuments.notGenerated'), meta: t('jobDocuments.createdFirstTime') };
    };

    return (
        <div className="jdoc">
            <div className="jdoc-head">
                <span className="lhs"><i className="fa-solid fa-file-lines"></i> {t('jobs.documents')}</span>
                {isLoading && <span className="jdoc-muted">{t('common.loading')}</span>}
            </div>
            {error && <div className="jdoc-error">{error}</div>}

            {TYPES.map((type) => {
                const status = statusOf(type);
                const canOpenReceipt = type !== 'PICKUP_RECEIPT' || !!(receiptDigital || receiptPaper);
                return (
                    <div className="jdoc-row" key={type}>
                        <div className="jdoc-info">
                            <div className="jdoc-title">
                                {DOCUMENT_LABELS[type]}
                                <span className={`jdoc-pill${status.ok ? ' ok' : ''}`}>{status.label}</span>
                            </div>
                            <div className="jdoc-status">{status.meta}</div>
                        </div>
                        <div className="jdoc-actions">
                            {type === 'PICKUP_RECEIPT' ? (
                                <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => setReceiptOpen(true)}>
                                    {receiptDigital ? t('jobDocuments.redoDigital') : t('jobDocuments.generate')}
                                </button>
                            ) : (
                                <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={!!busy} onClick={() => openPdf(type, false)}>
                                    {busy === `${type}-open` ? t('jobDocuments.opening') : type === 'WORK_ORDER' && !workOrderDoc ? t('jobDocuments.generateAndOpen') : t('jobDocuments.openPdf')}
                                </button>
                            )}
                            {type === 'WORK_ORDER' && (
                                <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={openCompletion}>
                                    <i className="fa-solid fa-pen-to-square" aria-hidden="true"></i> {t('jobDocuments.completionDetails')}
                                </button>
                            )}
                            {type === 'PICKUP_RECEIPT' && (
                                <IconButton icon="fa-file-pdf" label={t('jobDocuments.openPdf')} disabled={!!busy || !canOpenReceipt}
                                    hint={canOpenReceipt ? t('jobDocuments.openSignedReceipt') : t('jobDocuments.generateReceiptFirst')}
                                    busy={busy === `${type}-open`} onClick={() => openPdf(type, false)} />
                            )}
                            <IconButton icon="fa-print" label={t('sales.print')} disabled={!!busy || !canOpenReceipt}
                                hint={canOpenReceipt ? t('jobDocuments.openPrintDialog') : t('jobDocuments.generateReceiptFirst')}
                                busy={busy === `${type}-print`} onClick={() => openPdf(type, true)} />
                            <IconButton icon="fa-paper-plane" label={t('invoiceShare.send')}
                                hint={canOpenReceipt ? t('jobDocuments.sendByChannels') : t('jobDocuments.generateReceiptFirst')}
                                disabled={!!busy || !canOpenReceipt} busy={busy === `${type}-send`}
                                onClick={() => {
                                    if (type === 'INVOICE') return setInvoiceToSend({
                                        jobId,
                                        invoiceNumber,
                                        clientName: [client?.firstName, client?.lastName].filter(Boolean).join(' ') || t('jobs.client'),
                                        email: client?.email || '',
                                        phone: client?.phone || '',
                                    });
                                    if (type === 'PICKUP_RECEIPT') return sendReceipt();
                                    return sendWorkOrder();
                                }} />
                        </div>
                        {type === 'PICKUP_RECEIPT' && (
                            <div className="jdoc-sub">
                                <span className="jdoc-muted">{t('jobDocuments.paperReceipt')}</span>
                                <button type="button" className="jdoc-chip" disabled={!!busy} onClick={() => openBlank('A4')}>
                                    <i className="fa-solid fa-print" aria-hidden="true"></i> {t('jobDocuments.blankA4')}
                                </button>
                                <button type="button" className="jdoc-chip" disabled={!!busy} onClick={() => openBlank('A5')}>
                                    <i className="fa-solid fa-print" aria-hidden="true"></i> {t('jobDocuments.blankA5')}
                                </button>
                                <button type="button" className="jdoc-chip" disabled={!!busy} onClick={() => fileRef.current?.click()}>
                                    <i className={`fa-solid ${busy === 'scan' ? 'fa-spinner fa-spin' : 'fa-paperclip'}`} aria-hidden="true"></i>
                                    {busy === 'scan' ? t('jobDocuments.uploading') : t('jobDocuments.attachSignedPaper')}
                                </button>
                                <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" hidden onChange={onScanFile} />
                                {receiptPaper && receiptDigital && (
                                    <button type="button" className="jdoc-chip" disabled={!!busy} onClick={() => openPdf('PICKUP_RECEIPT', false, 'PAPER_SCAN')}>
                                        <i className="fa-solid fa-image" aria-hidden="true"></i> {t('jobDocuments.openPaperScan')}
                                    </button>
                                )}
                            </div>
                        )}
                        {type === 'WORK_ORDER' && completionOpen && completion && (
                            <div className="jdoc-form">
                                <label>{t('jobDocuments.completedAt')}<DateInput type="datetime-local" value={completion.completedAt} onChange={(e) => setCompletion({ ...completion, completedAt: e.target.value })} /></label>
                                <label>{t('jobDocuments.completedBy')}<input type="text" value={completion.completedBy} onChange={(e) => setCompletion({ ...completion, completedBy: e.target.value })} placeholder={t('workOrder.employee')} /></label>
                                <label className="wide">{t('jobDocuments.workPerformed')}<textarea rows="2" value={completion.workPerformed} onChange={(e) => setCompletion({ ...completion, workPerformed: e.target.value })} /></label>
                                <label className="wide">{t('jobDocuments.completionNotes')}<textarea rows="2" value={completion.notes} onChange={(e) => setCompletion({ ...completion, notes: e.target.value })} /></label>
                                <label>{t('jobDocuments.employeeConfirmation')}<input type="text" value={completion.employeeName} onChange={(e) => setCompletion({ ...completion, employeeName: e.target.value })} /></label>
                                <label>{t('jobDocuments.customerConfirmation')}<input type="text" value={completion.customerName} onChange={(e) => setCompletion({ ...completion, customerName: e.target.value })} /></label>
                                <label className="wide">{t('jobDocuments.customerRemarks')}<textarea rows="2" value={completion.customerRemarks} onChange={(e) => setCompletion({ ...completion, customerRemarks: e.target.value })} /></label>
                                <div className="jdoc-form-actions">
                                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => setCompletionOpen(false)}>{t('common.cancel')}</button>
                                    <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={!!busy} onClick={saveCompletion}>{busy === 'WORK_ORDER-save' ? t('common.saving') : t('common.save')}</button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {isOfficeUser() && <TrackingLinkControl jobId={jobId} client={client} stage={job?.status} onSendTracking={onSendTracking} />}

            <PickupReceiptModal show={receiptOpen} onHide={() => setReceiptOpen(false)} jobId={jobId} client={client} onSaved={refresh} />
            <InvoiceSharePanel invoice={invoiceToSend} onClose={() => setInvoiceToSend(null)} />
            <DocumentSharePanel document={documentToSend} client={client} onClose={() => setDocumentToSend(null)} />
        </div>
    );
};

export default JobDocumentsSection;
